import React from 'react';
import { Comment, CommentDeleted } from '../comment';
import { unreachable } from '../../../ts-utils';
import { CommentBox } from '../comment-box';
import { useApi } from '../../context/api-context/use-api';
import { useConfiguration } from '../../context/configuration-context/use-configuration';
import { isCommentContentEqual } from '../../../common/utils/content-utils';
import { getIsMobile } from '../../../common/store/base-params/base-params-selectors';
import { isWithGuestFlow } from '../../../common/store/comments/comments-permissions';
import { useModal, DeleteCommentConfirmationModal, ReportCommentModal } from '../modals';
import { getKey, getHtmlId } from './id-utils';
import { ActionsMenu } from '../actions-menu';
import { share } from './share';
import { CommentFooter } from '../comment/footers/comment-footer';
import CommentErrorFooter from '../comment/footers/comment-error-footer';
import CommentPendingFooter from '../comment/footers/comment-pending-footer';
import { mapTopLevelCommentState } from '../../../common/store/comments/top-level-comments/top-level-comment-selectors';
import styles from './comment-state-switch.scss';
import { getOwner } from '../../../common/store/members/members-selectors';
import { useTranslate } from '../../context/api-context/translations/use-translate';
import { usePermissions } from '../../context/permissions-context/use-permissions';
const CommentStateSwitchNoMemo = ({ commentState: commentStateIN, currentRating, inputRating, isByCurrentOwner, memberState, interactionFetchState, }) => {
    const commentState = mapTopLevelCommentState(commentStateIN, memberState);
    const { resourceId, ctxFields, markLabels, disableReporting, ratings: ratingsConfig, } = useConfiguration();
    const t = useTranslate();
    const { updateComment, retryCreateComment, cancelCreatingComment, cancelEditingComment, isMobile, editComment, editCommentCreateError, biDiscardComment, biClickShowMoreBtn, setInputRating, owner, } = useApi((state, actions) => ({
        updateComment: actions.updateComment,
        cancelCreatingComment: actions.cancelCreatingComment,
        cancelEditingComment: actions.cancelEditingComment,
        retryCreateComment: actions.retryCreateComment,
        isMobile: getIsMobile(state),
        editComment: actions.editComment,
        editCommentCreateError: actions.editCommentCreateError,
        biDiscardComment: actions.biDiscardComment,
        biClickShowMoreBtn: actions.biClickShowMoreBtn,
        biClickPublishSelectBtn: actions.biClickPublishSelectBtn,
        setInputRating: actions.setInputRating,
        owner: getOwner(commentState, state.members),
    }));
    const hasBeenUpdated = React.useRef(false);
    const { getPermission } = usePermissions();
    const canChangeRating = ratingsConfig && getPermission({ action: 'RATE' }) && isByCurrentOwner;
    React.useEffect(() => {
        switch (commentState.type) {
            case 'COMMENT_UPDATE_PENDING':
                hasBeenUpdated.current = true;
                return;
            case 'COMMENT_EDITING':
            case 'COMMENT_CREATE_ERROR_EDITING':
            case 'COMMENT_CREATE_ERROR':
            case 'COMMENT_UPDATE_ERROR':
            case 'COMMENT_DELETED':
            case 'COMMENT_READY':
            case 'COMMENT_CREATE_PENDING':
            case 'COMMENT_DELETE_PENDING':
            case 'COMMENT_LIVE_PENDING':
                return;
            default: {
                throw unreachable(commentState);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commentState.type]);
    switch (commentState.type) {
        case 'COMMENT_CREATE_PENDING': {
            const { localId, content } = commentState;
            return (React.createElement(Comment, { key: getKey({ localId }), id: getHtmlId({ localId }), commentId: localId, content: content, owner: owner, isPending: true, isReply: false, createdAt: new Date().toISOString(), rating: ratingsConfig && { value: currentRating?.value, isPending: true }, actionsMenu: React.createElement(ActionsMenu, { showDummy: true }), disableShowMore: true, footer: React.createElement(CommentPendingFooter, null) }));
        }
        case 'COMMENT_CREATE_ERROR': {
            const { localId, content, errorMessage, asGuest, pendingRatingAction, authorName, email } = commentState;
            return (React.createElement(Comment, { key: getKey({ localId }), id: getHtmlId({ localId }), commentId: localId, content: content, owner: owner, isPending: false, isReply: false, isError: true, createdAt: new Date().toISOString(), rating: {
                    value: pendingRatingAction?.type === 'ADD' ? pendingRatingAction.value : undefined,
                    isPending: true,
                }, actionsMenu: null, disableShowMore: true, footer: React.createElement(CommentErrorFooter, { error: errorMessage, onRetry: () => {
                        retryCreateComment({
                            ctxFields,
                            content,
                            resourceId,
                            localId,
                            asGuest,
                            rating: pendingRatingAction,
                            authorName,
                            email,
                        });
                    }, onEdit: () => {
                        editCommentCreateError({ resourceId, localId, authorName, email });
                    } }) }));
        }
        case 'COMMENT_CREATE_ERROR_EDITING': {
            const { localId, content, authorName, email } = commentState;
            return (React.createElement(CommentBox, { className: styles.commentBox, key: getKey({ localId }), id: getHtmlId({ localId }), commentId: localId, isReply: false, isEdit: false, isInList: true, formName: `comment-${localId}`, mountExpanded: true, initialContent: content, initialAuthorName: authorName, initialEmail: email, withGuestFlow: isWithGuestFlow(getPermission({ action: 'CREATE_COMMENT' })), onSubmit: ({ content, rating }, _reset, asGuest) => {
                    retryCreateComment({
                        ctxFields,
                        content,
                        resourceId,
                        localId,
                        asGuest,
                        rating,
                        authorName,
                        email,
                    });
                }, onCancel: () => {
                    return {
                        type: 'PROMPT',
                        discard: 'CHANGES',
                        onConfirm: () => {
                            biDiscardComment({ type: 'comment', resourceId });
                            cancelCreatingComment({ localId, resourceId });
                        },
                    };
                }, rating: ratingsConfig && {
                    value: inputRating?.value,
                    isRequired: !!ratingsConfig.isRequired,
                    hasPrevious: !!currentRating?.value,
                }, ratingLabel: ratingsConfig
                    ? ratingsConfig.addRatingText
                        ? ratingsConfig.addRatingText
                        : t(currentRating?.value ? 'comment-box.update-rating' : 'comment-box.add-a-rating')
                    : undefined, onRatingChange: canChangeRating ? (rating) => setInputRating({ resourceId, rating }) : undefined }));
        }
        case 'COMMENT_READY': {
            const { comment, localId, commentId, isLive, marked, isModerationPending } = commentState;
            if (comment.status === 'DELETED' || comment.status === 'HIDDEN') {
                return (React.createElement(CommentDeleted, { key: getKey({ commentId, localId }), id: getHtmlId({ commentId, localId }) }));
            }
            return (React.createElement(Comment, { key: getKey({ commentId, localId }), id: getHtmlId({ commentId, localId }), commentId: commentId, isMarked: marked.isMarked, customBadgeLabel: markLabels?.badgeLabel, customBadgeIcon: markLabels?.icon, content: isModerationPending ? comment.draftContent?.richContent : comment.content?.richContent, owner: owner, author: comment.author, createdAt: comment.createdAt, isReply: false, isLive: isLive, isPending: marked.pending, isModerationPending: isModerationPending, isEdited: comment.isEdited, rating: ratingsConfig && {
                    value: isByCurrentOwner && interactionFetchState === 'fetched'
                        ? currentRating?.value
                        : comment.rating,
                    isPending: isByCurrentOwner ? currentRating?.isPending : false,
                }, actionsMenu: React.createElement(ActionsMenuCommentReady, { commentId: commentId, comment: comment, isMobile: isMobile, marked: marked, commentByCurrentOwner: isByCurrentOwner, disableReporting: disableReporting }), onShowMoreClick: (isShowMore) => {
                    biClickShowMoreBtn({ isShowMore, resourceId, commentId });
                }, disableShowMore: !!localId || hasBeenUpdated.current, footer: isModerationPending ? null : (React.createElement(CommentFooter, { commentId: commentId, reactionsState: commentState.reactions, votesState: commentState.votes, canReact: getPermission({ action: 'REACT' }).type === 'ALLOW', canVote: getPermission({ action: 'VOTE' }).type === 'ALLOW', replyButtonState: getPermission({ action: 'CREATE_REPLY' }).type === 'ALLOW' ? 'enabled' : 'hidden', interactionFetchState: interactionFetchState })) }));
        }
        case 'COMMENT_DELETED': {
            const { commentId, localId } = commentState;
            return (React.createElement(CommentDeleted, { key: getKey({ commentId, localId }), id: getHtmlId({ commentId, localId }) }));
        }
        case 'COMMENT_EDITING': {
            const { marked, commentId, localId, comment, content } = commentState;
            const initialContent = content || comment.content?.richContent || comment.draftContent?.richContent;
            return (React.createElement(CommentBox, { className: styles.commentBox, key: getKey({ commentId, localId }), id: getHtmlId({ commentId, localId }), commentId: commentId, isReply: false, isEdit: true, isInList: true, isMarked: marked.isMarked, initialContent: initialContent, formName: `comment-${commentId}`, mountExpanded: true, onSubmit: ({ content, rating }, _reset) => {
                    updateComment({
                        content,
                        resourceId,
                        ctxFields,
                        commentId,
                        revisionId: comment.revisionId,
                        rating,
                        commentByCurrentOwner: isByCurrentOwner,
                        currentComment: comment,
                    });
                }, onCancel: (content) => {
                    if (isCommentContentEqual(content, comment.content?.richContent)) {
                        return {
                            type: 'SILENT_CANCEL',
                            cleanup: () => cancelEditingComment({ resourceId, commentId }),
                        };
                    }
                    else {
                        return {
                            type: 'PROMPT',
                            discard: 'CHANGES',
                            onConfirm: () => {
                                biDiscardComment({ type: 'comment', resourceId });
                                cancelEditingComment({ resourceId, commentId });
                            },
                        };
                    }
                }, rating: ratingsConfig && {
                    value: isByCurrentOwner && interactionFetchState === 'fetched'
                        ? inputRating?.value
                        : comment.rating,
                    isRequired: isByCurrentOwner ? !!ratingsConfig.isRequired : false,
                    hasPrevious: isByCurrentOwner ? !!currentRating?.value : false,
                }, ratingLabel: ratingsConfig && isByCurrentOwner
                    ? ratingsConfig.addRatingText
                        ? ratingsConfig.addRatingText
                        : t(currentRating?.value ? 'comment-box.update-rating' : 'comment-box.add-a-rating')
                    : undefined, onRatingChange: canChangeRating ? (rating) => setInputRating({ resourceId, rating }) : undefined }));
        }
        case 'COMMENT_UPDATE_PENDING': {
            const { commentId, marked, localId, content, comment } = commentState;
            return (React.createElement(Comment, { key: getKey({ commentId, localId }), id: getHtmlId({ commentId, localId }), author: comment.author, commentId: commentId, content: content, owner: owner, isPending: true, isReply: false, rating: ratingsConfig && currentRating && { ...currentRating }, createdAt: comment.createdAt, actionsMenu: React.createElement(ActionsMenu, { showDummy: true }), disableShowMore: true, footer: React.createElement(CommentPendingFooter, null), isMarked: marked.isMarked, isEdited: comment.isEdited }));
        }
        case 'COMMENT_UPDATE_ERROR': {
            const { marked, commentId, localId, content, comment, errorMessage, pendingRatingAction } = commentState;
            return (React.createElement(Comment, { key: getKey({ commentId, localId }), id: getHtmlId({ commentId, localId }), author: comment.author, commentId: commentId, content: content, owner: owner, isPending: false, isError: true, isEdited: comment.isEdited, isReply: false, createdAt: comment.createdAt, isMarked: marked.isMarked, actionsMenu: React.createElement(ActionsMenuCommentReady, { commentId: commentId, comment: comment, isMobile: isMobile, marked: marked, commentByCurrentOwner: isByCurrentOwner, disableReporting: disableReporting }), disableShowMore: true, rating: {
                    value: pendingRatingAction?.type === 'ADD' ? pendingRatingAction.value : undefined,
                    isPending: true,
                }, footer: React.createElement(CommentErrorFooter, { error: errorMessage, onRetry: () => {
                        updateComment({
                            ctxFields,
                            content,
                            resourceId,
                            commentId,
                            revisionId: comment.revisionId,
                            rating: pendingRatingAction,
                            commentByCurrentOwner: isByCurrentOwner,
                            currentComment: comment,
                        });
                    }, onEdit: () => {
                        editComment({ resourceId, commentId }, { commentByCurrentOwner: isByCurrentOwner });
                    } }) }));
        }
        case 'COMMENT_LIVE_PENDING': {
            const { commentId, content } = commentState;
            return (React.createElement(Comment, { key: getKey({ commentId }), id: getHtmlId({ commentId }), commentId: commentId, content: content, owner: owner, isPending: true, isReply: false, isLive: true, createdAt: new Date().toISOString(), actionsMenu: React.createElement(ActionsMenu, { showDummy: true }), footer: React.createElement(CommentFooter, { commentId: commentId, reactionsState: { total: 0, pending: false, reactions: [] }, votesState: { pending: false, state: 'NEUTRAL', score: 0 }, canReact: false, canVote: false, replyButtonState: "disabled", interactionFetchState: interactionFetchState }) }));
        }
        case 'COMMENT_DELETE_PENDING': {
            const { commentId, localId } = commentState;
            return (React.createElement(CommentDeleted, { isPending: true, key: getKey({ commentId, localId }), id: getHtmlId({ commentId, localId }) }));
        }
        default: {
            throw unreachable(commentState);
        }
    }
};
const ActionsMenuCommentReady = ({ commentId, marked, comment, isMobile, commentByCurrentOwner, disableReporting }) => {
    const { resourceId, deepLink } = useConfiguration();
    const { getPermission } = usePermissions();
    const showModal = useModal();
    const { editComment, deleteComment, biSelectCommentAction, biOpenCommentActions, showToast, markComment, reportComment, } = useApi((_state, actions) => ({
        deleteComment: actions.deleteComment,
        editComment: actions.editComment,
        markComment: actions.markComment,
        biSelectCommentAction: actions.biSelectCommentAction,
        biOpenCommentActions: actions.biOpenCommentActions,
        showToast: actions.showToast,
        reportComment: actions.reportComment,
    }));
    return (React.createElement(ActionsMenu, { onShowMenu: () => biOpenCommentActions({ type: 'comment', resourceId, commentId }), edit: getPermission({ action: 'UPDATE', comment }).type === 'ALLOW'
            ? () => {
                biSelectCommentAction({
                    type: 'comment',
                    action: 'edit',
                    resourceId,
                    commentId,
                });
                editComment({ resourceId, commentId }, { commentByCurrentOwner });
            }
            : undefined, mark: getPermission({ action: 'MARK' }).type === 'ALLOW' && marked && !marked.pending
            ? {
                isMarked: marked.isMarked,
                action: () => {
                    biSelectCommentAction({
                        type: 'comment',
                        action: marked.isMarked ? 'unmark' : 'mark',
                        resourceId,
                        commentId,
                    });
                    markComment({
                        resourceId,
                        commentId,
                        subaction: marked.isMarked ? 'REMOVE' : 'ADD',
                    });
                },
            }
            : undefined, delete: getPermission({ action: 'DELETE', comment }).type === 'ALLOW'
            ? () => {
                biSelectCommentAction({
                    type: 'comment',
                    action: 'delete',
                    resourceId,
                    commentId,
                });
                showModal((toggle) => (React.createElement(DeleteCommentConfirmationModal, { isReply: false, toggle: toggle, onClose: (confirmed) => {
                        confirmed && deleteComment({ resourceId, commentId });
                    } })));
            }
            : undefined, share: deepLink
            ? () => {
                biSelectCommentAction({
                    type: 'comment',
                    action: 'share',
                    resourceId,
                    commentId,
                });
                share({
                    url: deepLink.generateLink(commentId),
                    isMobile,
                    afterCopy: () => showToast({
                        skin: 'success',
                        message: { type: 'TRANSLATION', key: 'toast.share-success' },
                        resourceId,
                    }),
                });
            }
            : undefined, report: getPermission({ action: 'REPORT' }).type === 'ALLOW' && !disableReporting
            ? () => showModal((toggle) => (React.createElement(ReportCommentModal, { onClose: (reason, details) => {
                    if (reason) {
                        reportComment({ reason, details, commentId, resourceId });
                    }
                    toggle();
                } })))
            : undefined }));
};
export const CommentStateSwitch = React.memo(CommentStateSwitchNoMemo);
//# sourceMappingURL=comment-state-switch.js.map